import { useEffect, useRef, useState, useCallback } from "react";
import { logExperience } from "@/lib/experience";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { X, Volume2, VolumeX, Waves } from "lucide-react";
import ImmersionV2 from "@/pages/ImmersionV2";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EDGES = [
  "top-[14%] left-[8%]", "top-[20%] right-[9%] text-right", "bottom-[26%] left-[9%]",
  "bottom-[20%] right-[8%] text-right", "top-[42%] left-[6%]", "top-[52%] right-[7%] text-right",
];

function splitPhases(affs) {
  if (!affs?.length) return [["I am becoming who I choose to be"]];
  const size = Math.ceil(affs.length / 5);
  return Array.from({ length: 5 }, (_, i) => affs.slice(i * size, (i + 1) * size)).filter((g) => g.length);
}

export default function ImmersionPlayer() {
  const { topicId, envId } = useParams();
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [phases, setPhases] = useState(null);
  const [flashWords, setFlashWords] = useState([]);
  const [topicTitle, setTopicTitle] = useState("");
  const [started, setStarted] = useState(false);
  const [settling, setSettling] = useState(true);
  const [ending, setEnding] = useState(false);
  const [muted, setMuted] = useState(false);
  const [binaural, setBinaural] = useState(true);
  const [peripheral, setPeripheral] = useState(null);
  const [flash, setFlash] = useState(null);
  const [progress, setProgress] = useState(0);

  const videoRef = useRef(null);
  const phaseRef = useRef(0);
  const timers = useRef([]);
  const audioCtxRef = useRef(null);
  const binauralNodesRef = useRef(null);
  const musicRef = useRef(null);

  useEffect(() => {
    api.get(`/immersions/${envId}`).then((r) => setEnv(r.data)).catch(() => navigate("/immersions"));
    api.get("/music/bed", { params: { kind: "immersion", ref: envId } }).then((r) => {
      if (r.data.url) { const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`); a.loop = true; a.volume = 0.4; a.preload = "auto"; musicRef.current = a; }
    }).catch(() => {});
    api.get(`/topics/${topicId}/affirmations`).then((r) => {
      setPhases(splitPhases(r.data.affirmations));
      setFlashWords(r.data.flash?.length ? r.data.flash : ["calm", "clear", "worthy", "open", "free"]);
      setTopicTitle(r.data.topic?.title || "");
    }).catch(() => navigate("/immersions"));
  }, [topicId, envId, navigate]);

  const clearTimers = () => { timers.current.forEach((t) => { clearInterval(t); clearTimeout(t); }); timers.current = []; };
  const stopBinaural = () => { try { binauralNodesRef.current?.forEach((o) => o.stop()); } catch {} try { audioCtxRef.current?.close(); } catch {} audioCtxRef.current = null; binauralNodesRef.current = null; };

  const startBinaural = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const gain = ctx.createGain(); gain.gain.value = 0.05; gain.connect(ctx.destination);
      const merger = ctx.createChannelMerger(2); merger.connect(gain);
      const oscL = ctx.createOscillator(); oscL.frequency.value = 200; oscL.connect(merger, 0, 0);
      const oscR = ctx.createOscillator(); oscR.frequency.value = 207; oscR.connect(merger, 0, 1); // 7Hz theta
      oscL.start(); oscR.start();
      binauralNodesRef.current = [oscL, oscR];
    } catch {}
  };

  const onTime = useCallback(() => {
    const v = videoRef.current; if (!v || !env) return;
    const dur = v.duration || env.duration;
    const p = Math.min(1, v.currentTime / dur);
    setProgress(p);
    phaseRef.current = Math.min(4, Math.floor(p * 5));
    if (dur - v.currentTime < 7 && !ending) { setEnding(true); logExperience("immersion", `${topicId}/${env.id || ""}`, env.title || env.name || ""); }
  }, [env, ending]);

  const begin = async () => {
    const v = videoRef.current; if (!v || !phases) return;
    setStarted(true);
    musicRef.current?.play().catch(() => {}); // music is always part of the experience
    if (binaural) startBinaural();
    try { await v.play(); } catch {}
    setTimeout(() => setSettling(false), 4200);
    const perif = setInterval(() => {
      const ph = phases[Math.min(phaseRef.current, phases.length - 1)] || phases[0];
      setPeripheral({ text: ph[Math.floor(Math.random() * ph.length)], edge: EDGES[Math.floor(Math.random() * EDGES.length)], key: Date.now() });
    }, 6500);
    const flashTick = () => {
      const w = flashWords[Math.floor(Math.random() * flashWords.length)];
      setFlash({ text: w, key: Date.now() });
      timers.current.push(setTimeout(() => setFlash(null), 130));
      timers.current.push(setTimeout(flashTick, 1800 + Math.random() * 3200));
    };
    timers.current.push(perif, setTimeout(flashTick, 3000));
  };

  useEffect(() => () => { clearTimers(); stopBinaural(); musicRef.current?.pause(); }, []);

  const exit = () => { clearTimers(); stopBinaural(); musicRef.current?.pause(); navigate(`/immersions/${topicId}`); };
  const toggleMute = () => { const v = videoRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); if (musicRef.current) musicRef.current.muted = v.muted; } };
  const toggleBinaural = () => {
    if (binaural) { stopBinaural(); setBinaural(false); }
    else { setBinaural(true); if (started) startBinaural(); }
  };

  if (!env || !phases) return <div className="fixed inset-0 bg-black" />;
  if (env.v2) return <ImmersionV2 env={env} phases={phases} flash={flashWords} topicId={topicId} topicTitle={topicTitle} />;
  const accent = env.accent || "#E39AA6";
  const integration = phases[phases.length - 1]?.[0] || "You return as yourself";

  return (
    <div className="fixed inset-0 z-[70] bg-black overflow-hidden" data-testid="immersion-player">
      <video ref={videoRef} src={`${BACKEND_URL}${env.video_url}`} onTimeUpdate={onTime} onEnded={() => setEnding(true)}
        playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover" data-testid="immersion-video" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)" }} />
      <div className="absolute inset-0 pointer-events-none grain opacity-40" />

      <AnimatePresence>
        {started && peripheral && (
          <motion.div key={peripheral.key} initial={{ opacity: 0 }} animate={{ opacity: 0.22 }} exit={{ opacity: 0 }} transition={{ duration: 2.2, ease: "easeInOut" }}
            className={`absolute ${peripheral.edge} max-w-[42%] font-serif text-xl sm:text-2xl text-white select-none pointer-events-none`}
            style={{ filter: "blur(0.6px)", textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}>{peripheral.text}</motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {started && flash && (
          <motion.div key={flash.key} initial={{ opacity: 0 }} animate={{ opacity: 0.16 }} exit={{ opacity: 0 }} transition={{ duration: 0.06 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="font-serif text-white/90 text-4xl tracking-[0.3em] uppercase" style={{ transform: `translate(${(Math.random() - 0.5) * 120}px, ${(Math.random() - 0.5) * 120}px)` }}>{flash.text}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {started && settling && (
          <motion.div initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.6 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6">
            <motion.p animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 4, repeat: Infinity }} className="font-serif text-2xl text-white/90">Breathe. Settle in.</motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {!started && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="immersion-entry"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.5))" }}>
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>{topicTitle} · {env.theme}</p>
          <h1 className="font-serif text-4xl sm:text-6xl text-white mb-3">{env.title}</h1>
          <p className="text-white/70 max-w-md mb-8 leading-relaxed">Find a comfortable position. Headphones on if you can. There's nothing to do but watch and let it in.</p>
          <Button size="lg" className="rounded-full px-8" onClick={begin} data-testid="immersion-begin-btn">Begin</Button>
        </div>
      )}

      <AnimatePresence>
        {ending && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 3 }} className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center text-center px-6" data-testid="immersion-close">
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 2 }} className="font-serif text-3xl sm:text-4xl text-white mb-8 max-w-lg">{integration}</motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}>
              <Button size="lg" variant="secondary" className="rounded-full px-8" onClick={exit} data-testid="immersion-return-btn">Return</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {started && !ending && (
        <>
          <div className="absolute top-0 inset-x-0 h-1 bg-white/10"><div className="h-full transition-[width] duration-1000" style={{ width: `${progress * 100}%`, background: accent }} /></div>
          <div className="absolute bottom-6 left-6 flex gap-2">
            <button onClick={toggleMute} className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:text-white transition-colors" data-testid="immersion-mute">
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button onClick={toggleBinaural} className={`w-11 h-11 rounded-full backdrop-blur-md flex items-center justify-center transition-colors ${binaural ? "bg-white/20 text-white" : "bg-white/10 text-white/50"}`} data-testid="immersion-binaural" title="Binaural beats">
              <Waves className="w-5 h-5" />
            </button>
          </div>
        </>
      )}
      <button onClick={exit} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white transition-colors z-10" data-testid="immersion-exit">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
